/**
 * Submissions API endpoints
 */

import { fetchApi, buildQueryString, withPagination } from '../core';
import type { QueryParams, CreateSubmissionData, UpdateSubmissionData, GradeSubmissionData, DetailedGradingResults } from '../types';
import type { Submission, PaginatedResponse } from '@/types';
import { objectIdPath } from '../path';

type SubmissionListResponse = PaginatedResponse<Submission[]> | Submission[];

export function getAll(params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<PaginatedResponse<Submission[]>>(`/submissions${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getForAssignment(assignmentId: string, params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<SubmissionListResponse>(`/assignments/${objectIdPath(assignmentId, 'assignment')}/submissions${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getForStudent(studentId: string, params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<SubmissionListResponse>(`/students/${objectIdPath(studentId, 'student')}/submissions${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getForCourseStudent(courseId: string, studentId: string, params?: QueryParams, componentName: string = 'Unknown') {
  const paginatedParams = withPagination(params);
  return fetchApi<SubmissionListResponse>(`/courses/${objectIdPath(courseId, 'course')}/students/${objectIdPath(studentId, 'student')}/submissions${buildQueryString(paginatedParams)}`, {}, componentName);
}

export function getById(id: string, componentName: string = 'Unknown') {
  return fetchApi<Submission>(`/submissions/${objectIdPath(id, 'submission')}`, {}, componentName);
}

export function create(data: CreateSubmissionData, componentName: string = 'Unknown') {
  return fetchApi<Submission>('/submissions', {
    method: 'POST',
    body: JSON.stringify(data)
  }, componentName);
}

export function update(id: string, data: UpdateSubmissionData, componentName: string = 'Unknown') {
  return fetchApi<Submission>(`/submissions/${objectIdPath(id, 'submission')}`, {
    method: 'PUT',
    body: JSON.stringify(data)
  }, componentName);
}

export function grade(id: string, data: GradeSubmissionData, componentName: string = 'Unknown') {
  return fetchApi<Submission>(`/submissions/${objectIdPath(id, 'submission')}/grade`, {
    method: 'POST',
    body: JSON.stringify(data)
  }, componentName);
}

export function getResults(id: string, componentName: string = 'Unknown') {
  return fetchApi<DetailedGradingResults>(`/submissions/${objectIdPath(id, 'submission')}/results`, {}, componentName);
}

export function remove(id: string, componentName: string = 'Unknown') {
  return fetchApi(`/submissions/${objectIdPath(id, 'submission')}`, { method: 'DELETE' }, componentName);
}
